import { useState } from "react";
import { ArrowLeft, CheckCircle2, FlaskConical, Plus, ExternalLink, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageFooter } from "@/components/PageFooter";
import { getOwnerByCountry } from "@/data/contacts";
import { useLang } from "@/i18n";
const countryData = [
  {
    country: "HR",
    name: "Croatia",
    status: "Live",
    metric: "3,488 outlets",
    statusColor: "live",
    description:
      "Largest deployment. QR menu across HoReCa chains, restaurants and bars. POS + Order & Pay. Multilingual (HR/EN).",
    tags: ["POS integration", "Order & Pay", "Multilingual", "Allergens", "AvailIT", "Config"],
    demoUrl: "https://qr.master-fb.com/XCVQ4ZJX",
  },
  {
    country: "BA",
    name: "Bosnia & Herzegovina",
    status: "Live",
    metric: "890 outlets",
    statusColor: "live",
    description:
      "Full deployment across Bosnia & Herzegovina. QR menu active across HoReCa venues.",
    tags: ["QR Menu", "Multilingual"],
    demoUrl: "https://qr.master-fb.com/RHYH8N9T",
  },
  {
    country: "PL",
    name: "Poland",
    status: "Live",
    metric: "786 outlets",
    statusColor: "live",
    description:
      "Growing rollout across major cities. POS integration active. Payless ordering scenario live.",
    tags: ["POS integration", "Payless", "Multilingual", "GOPOS", "Noome"],
    demoUrl: "https://qr.master-fb.com/SBGV4OFL",
  },
  {
    country: "SI",
    name: "Slovenia",
    status: "Live",
    metric: "766 outlets",
    statusColor: "live",
    description:
      "Full deployment across Slovenian venues. QR menu with multilingual support (SI/EN).",
    tags: ["Multilingual", "QR Menu"],
    demoUrl: "https://qr.master-fb.com/ALT1TCKA",
  },
  {
    country: "MK",
    name: "North Macedonia",
    status: "Live",
    metric: "683 outlets",
    statusColor: "live",
    description:
      "QR menu deployment across North Macedonian HoReCa venues with local language support.",
    tags: ["QR Menu", "Multilingual"],
    demoUrl: "https://qr.master-fb.com/O1CHLI6F",
  },
  {
    country: "RS",
    name: "Serbia",
    status: "Live",
    metric: "29 outlets",
    statusColor: "live",
    description:
      "QR menu active across Serbian venues. Multilingual support (Serbian Latin / English).",
    tags: ["QR Menu", "Multilingual"],
    demoUrl: "https://qr.master-fb.com/LQXTVWWF",
  },
  {
    country: "RO",
    name: "Romania",
    status: "Live",
    metric: "29 outlets",
    statusColor: "live",
    description:
      "New market deployment in Romania. QR menu active with local language support.",
    tags: ["QR Menu", "Multilingual"],
    demoUrl: "",
  },
];

const marketsSidebar = [
  { code: "all", label: "All markets" },
  { code: "HR", label: "HR Croatia" },
  { code: "BA", label: "BA Bosnia" },
  { code: "PL", label: "PL Poland" },
  { code: "SI", label: "SI Slovenia" },
  { code: "MK", label: "MK N. Macedonia" },
  { code: "RS", label: "RS Serbia" },
  { code: "RO", label: "RO Romania" },
];

export const CountryExamples = ({ onNavigate }) => {
  const { t } = useLang();
  const [activeMarket, setActiveMarket] = useState("all");

  const filtered =
    activeMarket === "all"
      ? countryData
      : countryData.filter((c) => c.country === activeMarket);

  return (
    <div data-testid="country-examples-view">
      {/* Back link */}
      <button
        onClick={() => onNavigate("product")}
        className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#555555] hover:text-[#dd260e] mb-10 transition-colors"
        data-testid="back-to-product-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("Digital Menu")}
      </button>

      {/* Title */}
      <div className="mb-10">
        <span className="cchbc-eyebrow mb-5">{t("Deployments by Country")}</span>
        <h1
          className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95]"
          data-testid="country-examples-title"
        >
          {t("Digital Menu — country examples")}
        </h1>
        <p className="text-[#555555] text-base leading-relaxed max-w-2xl">
          {t("How this product is used across markets so far")}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Markets sub-sidebar — horizontal on mobile, vertical on desktop */}
        <div className="lg:w-52 shrink-0" data-testid="markets-sidebar">
          <span className="cchbc-eyebrow mb-4">{t("Markets")}</span>
          <nav className="flex flex-wrap lg:flex-col gap-1 lg:space-y-0.5 mt-4">
            {marketsSidebar.map((m) => (
              <button
                key={m.code}
                onClick={() => setActiveMarket(m.code)}
                className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
                  activeMarket === m.code
                    ? "text-[#dd260e] font-semibold bg-[#dd260e]/5"
                    : "text-[#555555] hover:text-[#000000] hover:bg-[#fafafa]"
                }`}
                data-testid={`market-filter-${m.code.toLowerCase()}`}
              >
                {t(m.label)}
              </button>
            ))}
          </nav>
          <Separator className="my-4 bg-[#e8e8e8]" />
          <button
            className="text-sm text-[#555555] hover:text-[#dd260e] font-medium flex items-center gap-1.5 px-3 transition-colors"
            data-testid="add-market-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("Add market")}
          </button>
        </div>

        {/* Country Cards */}
        <div className="flex-1 space-y-5" data-testid="country-cards-container">
          {filtered.map((country) => (
            <div
              key={country.country}
              className="bg-white border border-[#e8e8e8] rounded-sm hover:border-[#dd260e] transition-colors"
              data-testid={`country-card-${country.country.toLowerCase()}`}
            >
              <div className="p-6">
                {/* Header row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-[#000000] text-white text-xs font-bold rounded-sm">
                      {country.country}
                    </span>
                    <div>
                      <p className="font-bold text-[#000000] text-base">
                        {t(country.name)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={`rounded-sm border-transparent text-xs font-semibold ${
                        country.statusColor === "live"
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                          : "bg-amber-50 text-amber-700 hover:bg-amber-50"
                      }`}
                      data-testid={`status-badge-${country.country.toLowerCase()}`}
                    >
                      {country.statusColor === "live" ? (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      ) : (
                        <FlaskConical className="w-3 h-3 mr-1" />
                      )}
                      {t(country.status)}
                    </Badge>
                    <Badge
                      className="bg-[#e8e8e8] text-[#000000] border-transparent rounded-sm text-xs font-semibold hover:bg-[#e8e8e8]"
                      data-testid={`metric-badge-${country.country.toLowerCase()}`}
                    >
                      {country.metric.replace("outlets", t("outlets"))}
                    </Badge>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-[#555555] leading-relaxed mb-4">
                  {t(country.description)}
                </p>

                {/* Feature tags + demo link */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-wrap gap-1.5">
                    {country.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium bg-[#fafafa] border border-[#e8e8e8] text-[#555555] rounded-sm"
                        data-testid={`feature-tag-${tag.toLowerCase().replace(/[\s&]+/g, "-")}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  {country.demoUrl && (
                    <a
                      href={country.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-[#dd260e] font-semibold hover:underline underline-offset-2 transition-colors shrink-0 ml-4"
                      data-testid={`demo-link-${country.country.toLowerCase()}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {t("Live demo")}
                    </a>
                  )}
                </div>

                {/* CC Country Owner */}
                {(() => {
                  const owner = getOwnerByCountry(country.country);
                  return owner && owner.email ? (
                    <div className="pt-3 border-t border-[#e8e8e8] flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-[#555555]" />
                      <span className="text-xs text-[#555555]">{t("CC Owner:")}</span>
                      <a href={`mailto:${owner.email}`} className="text-xs text-[#dd260e] font-semibold hover:underline">
                        {owner.owner}
                      </a>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          ))}

          {/* Admin add button */}
          <button
            className="w-full border border-dashed border-[#e8e8e8] rounded-sm py-4 text-sm text-[#555555] hover:border-[#dd260e] hover:text-[#dd260e] transition-colors font-medium flex items-center justify-center gap-2"
            data-testid="add-country-example-btn"
          >
            <Plus className="w-4 h-4" />
            {t("Add country example (admin only)")}
          </button>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
